import { Proposals } from "@repo/core/components/layout/ProposalGroup/types";
import { mapProposalToCard } from "@repo/core/mappers/proposals";
import {
  ProposalGroupedByDateItem,
  ProposalTypeValue
} from "@repo/core/services/entities/Proposals";
import { WayupProposalSearchItemDTO } from "@repo/core/services/entities/WayupRequisitions";
import { formatDateWithCurrentDay } from "@repo/core/utils/date.utils";

const readSearchField = (
  item: Record<string, unknown>,
  field: string
): string => {
  const direct = item[field];
  if (direct != null && String(direct).trim() !== "") {
    return String(direct).trim();
  }

  const match = Object.keys(item).find(
    (key) => key.toLowerCase() === field.toLowerCase()
  );
  if (!match || item[match] == null) {
    return "";
  }

  return String(item[match]).trim();
};

export const extractProposalSearchItems = (
  payload: unknown
): WayupProposalSearchItemDTO[] => {
  if (Array.isArray(payload)) {
    return payload as WayupProposalSearchItemDTO[];
  }

  if (!payload || typeof payload !== "object") {
    return [];
  }

  const record = payload as Record<string, unknown>;
  const nested = [record.data, record.dados, record.result, record.items].find(
    Array.isArray
  );

  return (nested as WayupProposalSearchItemDTO[] | undefined) ?? [];
};

const parseSearchResultDate = (isoDate: string): Date | null => {
  const datePart = isoDate?.split("T")[0];
  if (!datePart) {
    return null;
  }

  if (datePart.includes("/")) {
    const [day, month, year] = datePart.split("/").map(Number);
    if (!year || !month || !day) {
      return null;
    }
    return new Date(year, month - 1, day);
  }

  const [year, month, day] = datePart.split("-").map(Number);
  if (!year || !month || !day) {
    return null;
  }

  return new Date(year, month - 1, day);
};

const toGroupedByDateItem = (
  item: WayupProposalSearchItemDTO
): ProposalGroupedByDateItem => {
  const record = item as unknown as Record<string, unknown>;

  return {
    lngProposta: Number(readSearchField(record, "lngproposta")),
    strSituacao: readSearchField(record, "strsituacao"),
    strTipoProposta: readSearchField(
      record,
      "strtipoproposta"
    ) as ProposalTypeValue,
    datDataSituacao: readSearchField(record, "datdatasituacao"),
    strSituacaoDescricao: readSearchField(record, "strsituacaodescricao"),
    strColor: "",
    strContraste: "",
    strCor: "",
    strNome: readSearchField(record, "strnome"),
    strBusca: "",
    strProdutoDescricao: readSearchField(record, "strprodutodescricao"),
    strProduto: readSearchField(record, "strproduto"),
    strCliente: readSearchField(record, "strcliente")
  };
};

export const mapProposalSearchResultsToGroups = (
  payload: unknown
): Proposals[] => {
  const items = extractProposalSearchItems(payload);
  if (!items.length) {
    return [];
  }

  const groupsByDate = new Map<string, Proposals>();

  for (const item of items) {
    const record = item as unknown as Record<string, unknown>;
    const date =
      parseSearchResultDate(readSearchField(record, "datdatasituacao")) ??
      new Date();
    const dateKey = `${date.getFullYear()}-${String(
      date.getMonth() + 1
    ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    const existingGroup = groupsByDate.get(dateKey);

    const card = mapProposalToCard(toGroupedByDateItem(item));

    if (existingGroup) {
      existingGroup.items.push(card);
      continue;
    }

    groupsByDate.set(dateKey, {
      date: formatDateWithCurrentDay(date),
      items: [card]
    });
  }

  return Array.from(groupsByDate.entries())
    .sort(([dateA], [dateB]) => (dateA < dateB ? 1 : -1))
    .map(([, group]) => ({
      ...group,
      items: [...group.items].sort(
        (a, b) => Number(b.proposalId) - Number(a.proposalId)
      )
    }));
};
